
// src/utils/format.js

/**
 * Format a metric value for display:
 * - Percentages: auto-detected via backendKey suffix "Pct", label containing "%", or known ratio keys → "12.34%"
 * - Ratios: label contains "Ratio" → 3 decimals (ONLY for non-percent ratios)
 * - Days: backendKey ends with "Days" or label contains "Days" → plain number (2 decimals)
 * - Large numbers: scaled to K/M/B/T with currency symbol (default "$")
 * - Null/undefined/"N/A": returns "N/A"
 *
 * @param {string} label       Human label (e.g., "Gross Profit Ratio", "Revenue Growth %")
 * @param {number|string} num  Value to format
 * @param {string|null} backendKey Backend field name (e.g., "grossMarginPct", "grossProfitRatio")
 * @param {object} opts        Options: { currencySymbol?: string, scale?: boolean }
 * @returns {string}
 */
export const formatNumber = (label, num, backendKey = null, opts = {}) => {
  const currencySymbol = typeof opts.currencySymbol === 'string' ? opts.currencySymbol : '$';
  const scale = opts.scale !== undefined ? !!opts.scale : true; // scale by default

  // Handle nullish and explicit "N/A"
  if (num === 'N/A' || num === null || num === undefined) return 'N/A';

  // Coerce to number; guard non-numeric
  const value = Number(num);
  if (!Number.isFinite(value)) return 'N/A';

  const labelSafe = typeof label === 'string' ? label.toLowerCase() : '';
  const keySafe = typeof backendKey === 'string' ? backendKey.toLowerCase() : '';

  // ----- Known ratio keys that should be formatted as percentages -----
  // These match your Metrics.js mapping: grossProfitRatio, ebitdaRatio, incomeBeforeTaxRatio,
  // netIncomeRatio, operatingIncomeRatio
  const PERCENT_RATIO_KEYS = new Set([
    'grossprofitratio',
    'ebitdaratio',
    'incomebeforetaxratio',
    'netincomeratio',
    'operatingincomeratio',
  ]);

  // Type detection
  const isExplicitPercent =
    (keySafe && keySafe.endsWith('pct')) || (labelSafe && labelSafe.includes('%'));

  // Treat "ratio" keys from the set above as percentages
  const isPercentRatioKey = keySafe && PERCENT_RATIO_KEYS.has(keySafe);

  // Generic "ratio" (3 decimals) for labels with "ratio" that are NOT percentage ratios
  const isGenericRatio = labelSafe.includes('ratio') && !isPercentRatioKey;

  const isDays =
    (keySafe && keySafe.endsWith('days')) || (labelSafe && /days/i.test(label));

  // Helpers
  const fmt2 = (n) =>
    Math.abs(n) >= 100
      ? Math.round(n).toLocaleString('en-GB')
      : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt3 = (n) =>
    n.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  // 1) Percentages (explicit or known ratio keys)
  if (isExplicitPercent || isPercentRatioKey) {
    // Auto-scale 0–1 values to 0–100%
    const pctValue = Math.abs(value) <= 1 ? value * 100 : value;
    // Keep sign
    const signedPct = value < 0 ? -pctValue : pctValue;
    return `${fmt2(signedPct)}%`;
  }

  // 2) Generic Ratios (3 decimals, NO %)
  if (isGenericRatio) return fmt3(value);

  // 3) Days (plain numeric)
  if (isDays) return fmt2(value);

  // 4) Explicit overrides (examples)
  const OVERRIDES = {
    earningspershare: 'plain', // EPS – plain numeric
    marketcap: 'scaled',       // ensure scaling for Market Cap
  };
  const override = keySafe ? OVERRIDES[keySafe] : null;

  // 5) Scaled currency (K/M/B/T) with symbol
  const asScaledCurrency = (n) => {
    const v = Math.abs(n);
    if (v >= 1.0e12) return `${currencySymbol}${(n / 1.0e12).toFixed(1)} T`;
    if (v >= 1.0e9)  return `${currencySymbol}${(n / 1.0e9).toFixed(1)} B`;
    if (v >= 1.0e6)  return `${currencySymbol}${(n / 1.0e6).toFixed(1)} M`;
    if (v >= 1.0e3)  return `${currencySymbol}${(n / 1.0e3).toFixed(1)} K`;
    return `${currencySymbol}${fmt2(n)}`;
  };

  // Decide formatting branch
  if (override === 'plain') return fmt2(value);
  if (override === 'scaled') return asScaledCurrency(value);

  // Heuristic: backend keys commonly scaled (currency-like)
  const looksCurrencyKey =
    keySafe &&
    (
    keySafe.includes('revenue') ||
    keySafe.includes('profit') ||
    keySafe.includes('ebitda') ||
    keySafe.includes('income') ||
    keySafe.includes('cash') ||
    keySafe.includes('fcf') ||
    keySafe.includes('capex') ||
    keySafe.includes('workingcapital') ||
    keySafe.includes('marketcap') ||

    // --- Balance Sheet Keys Below ---
    keySafe.includes('asset') ||
    keySafe.includes('liabilit') ||   // matches liability / liabilities
    keySafe.includes('equity') ||
    keySafe.includes('debt') ||
    keySafe.includes('receivable') ||
    keySafe.includes('payable') ||
    keySafe.includes('inventory') ||
    keySafe.includes('stock') ||
    keySafe.includes('earning')
    );

  if (scale && looksCurrencyKey) return asScaledCurrency(value);

  // Default: plain numeric 2 decimals
  return fmt2(value);
};


